/**
 * Utilitaires pour formater et exporter les données d'un lead auto
 */

import { DateTime } from "luxon";

// Questions fermées de l'étape 2 (PER-2428) : booléens dans productdata,
// « 1 » / « 2 » dans le formulaire, comme sinistres_36_mois.
export const QUESTIONS_OUI_NON_ETAPE_2 = [
  "conduite_accompagnee",
  "suspension_permis",
  "condamnation_alcool_stupefiants",
  "accident_responsable_alcool_stupefiants",
  "resiliation_sinistre",
  "resiliation_contentieux",
  "surendettement",
  "vehicule_deja_assure",
];

// Réponses de l'étape 2 stockées dans productdata avec un suffixe
// « _utilisateur » : elles ne viennent pas de user.
const CHAMPS_ETAPE_2_SUFFIXE_UTILISATEUR = [
  "adresse_utilisateur",
  "code_postal_utilisateur",
  "ville_utilisateur",
];

/**
 * Récupère la valeur d'une question depuis les données du lead
 */
export const getQuestionValue = (questionKey, productData, userData) => {
  if (
    questionKey === "commune_utilisateur" ||
    CHAMPS_ETAPE_2_SUFFIXE_UTILISATEUR.includes(questionKey)
  ) {
    return productData[questionKey];
  }

  if (questionKey.endsWith("_utilisateur")) {
    const userFieldMap = {
      civilite_utilisateur: "civilite",
      nom_utilisateur: "nom",
      prenom_utilisateur: "prenom",
      telephone_utilisateur: "telephone",
      email_utilisateur: "email",
    };
    const userField = userFieldMap[questionKey];
    return userField ? userData[userField] : null;
  }

  if (questionKey.startsWith("sinistre") && questionKey.includes("_")) {
    const matchPresence = questionKey.match(/sinistre(\d+)_presence/);
    if (matchPresence) {
      const sinistreNumber = parseInt(matchPresence[1]);
      const sinistresCount = productData.sinistres?.length || 0;
      return sinistresCount >= sinistreNumber ? "1" : "2";
    }

    const match = questionKey.match(/sinistre(\d+)_(nature|date|responsabilite|tiers_identifie)/);
    if (match) {
      const index = parseInt(match[1]) - 1;
      const field = match[2];
      const sinistre = productData.sinistres?.[index];
      // « Tiers identifié » (PER-2496) : booléen dans productdata, « 1 » / « 2 » dans le formulaire.
      if (field === "tiers_identifie") {
        if (sinistre?.tiers_identifie === true) return "1";
        if (sinistre?.tiers_identifie === false) return "2";
        return null;
      }
      return sinistre?.[field];
    }
  }

  if (questionKey.startsWith("garantie_") && questionKey.endsWith("_active")) {
    const garantieKey = questionKey.replace("_active", "");
    const value = productData[garantieKey];
    if (value === true) return "1";
    if (value === false) return "2";
    return null;
  }

  if (
    questionKey === "sinistres_36_mois" ||
    QUESTIONS_OUI_NON_ETAPE_2.includes(questionKey)
  ) {
    const value = productData[questionKey];
    if (value === true) return "1";
    if (value === false) return "2";
    return null;
  }

  return productData[questionKey];
};

/**
 * Formate une valeur selon le type de question
 */
export const formatValue = (questionKey, value, config) => {
  const DefaultValue =
    config.category === "garanties"
      ? "Option non sélectionnée"
      : "Non renseigné";

  if (value === null || value === undefined) return DefaultValue;

  switch (config.type) {
    case "BTN_LIST":
    case "SELECT":
      const option = config.datas?.find(
        (d) => d.id === value || d.id === String(value),
      );
      return option?.name || value;
    case "DATE":
      if (!value) return "Non renseigné";
      try {
        const date = new Date(value);
        return date.toLocaleDateString("fr-FR");
      } catch {
        return value;
      }
    case "NUMBER_INT":
      return value;
    default:
      return value;
  }
};

/**
 * Détermine si une question doit être affichée selon ses conditions
 */
export const shouldDisplayQuestion = (
  questionKey,
  config,
  productData,
  userData,
) => {
  if (!config.condition) return true;

  const evaluateSingle = (condition) => {
    if (Array.isArray(condition)) {
      return condition.every(evaluateSingle);
    }
    const { dependsOn, value: conditionValue, operator } = condition;
    const dependentValue = getQuestionValue(dependsOn, productData, userData);

    switch (operator) {
      case "equals":
        return (
          dependentValue === conditionValue ||
          String(dependentValue) === String(conditionValue)
        );
      case "not_equals":
        return (
          dependentValue !== conditionValue &&
          String(dependentValue) !== String(conditionValue)
        );
      case "in":
        return (
          Array.isArray(conditionValue) &&
          (conditionValue.includes(dependentValue) ||
            conditionValue.includes(String(dependentValue)))
        );
      case "not_in":
        return (
          Array.isArray(conditionValue) &&
          !conditionValue.includes(dependentValue) &&
          !conditionValue.includes(String(dependentValue))
        );
      case "within_months": {
        const monthsLimit = Number(conditionValue);
        if (isNaN(monthsLimit)) return false;
        const date = DateTime.fromISO(String(dependentValue));
        if (!date.isValid) return false;
        return date >= DateTime.now().minus({ months: monthsLimit });
      }
      default:
        return true;
    }
  };

  return evaluateSingle(config.condition);
};

/**
 * Construit un formState plat (identique au payload web) à partir des données d'un lead.
 * Utilise getQuestionValue pour chaque clé de questionsConfig.
 */
export const buildFormStateFromLeadData = (leadData, questionsConfig) => {
  const productData = leadData?.productdata || {};
  const userData = leadData?.user || {};
  const formState = {};

  Object.keys(questionsConfig).forEach((questionKey) => {
    const value = getQuestionValue(questionKey, productData, userData);
    formState[questionKey] = value !== null && value !== undefined ? value : "";
  });

  return formState;
};

/**
 * Génère le contenu texte complet pour l'export d'un lead
 */
export const generateLeadTextExport = (
  leadAutoItem,
  questionsConfig,
  stepsConfig,
) => {
  const productData = leadAutoItem?.productdata || {};
  const userData = leadAutoItem?.user || {};
  let textContent = "";

  // Section estimation de prime
  if (productData.prime_annuelle_ttc) {
    textContent += "=== ESTIMATION DE PRIME ===\n\n";
    textContent += "Prime annuelle TTC estimée\n";
    textContent += `${productData.prime_annuelle_ttc.toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €\n\n`;
    textContent += "Prime mensuelle\n";
    textContent += `${(productData.prime_annuelle_ttc / 12).toLocaleString(
      "fr-FR",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    )} €\n\n`;

    if (productData.fourchette_prime_annuelle_min) {
      textContent += "Fourchette basse\n";
      textContent += `${productData.fourchette_prime_annuelle_min.toLocaleString(
        "fr-FR",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      )} €\n\n`;
    }

    if (productData.fourchette_prime_annuelle_max) {
      textContent += "Fourchette haute\n";
      textContent += `${productData.fourchette_prime_annuelle_max.toLocaleString(
        "fr-FR",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      )} €\n\n`;
    }
  }

  // Questions sans catégorie (utilisateur)
  const questionsWithoutCategory = Object.entries(questionsConfig).filter(
    ([_, config]) => !config.category || config.category === "utilisateur",
  );

  if (questionsWithoutCategory.length > 0) {
    textContent += "=== INFORMATIONS UTILISATEUR ===\n\n";
    questionsWithoutCategory.forEach(([key, config]) => {
      if (shouldDisplayQuestion(key, config, productData, userData)) {
        const value = getQuestionValue(key, productData, userData);
        const formattedValue = formatValue(key, value, config);
        textContent += `${config.label}\n${formattedValue}\n\n`;
      }
    });
  }

  // Questions par étape
  stepsConfig.forEach((step) => {
    const stepQuestions = Object.entries(questionsConfig).filter(
      ([_, config]) => config.category === step.id,
    );

    if (stepQuestions.length > 0) {
      textContent += `=== ${step.title.toUpperCase()} ===\n\n`;
      stepQuestions.forEach(([key, config]) => {
        if (shouldDisplayQuestion(key, config, productData, userData)) {
          const value = getQuestionValue(key, productData, userData);
          const formattedValue = formatValue(key, value, config);
          textContent += `${config.label}\n${formattedValue}\n\n`;
        }
      });
    }
  });

  return textContent;
};
